import { Alert, Button, Divider, Modal, Typography } from 'antd';
import packageInfo from '../../package.json';
import axios from 'axios';
import { BASE_URL, CLEAR_SITE_DATA_FAQ } from 'shared/constants';
import { useEffect, useState } from 'react';

interface Props {
  onVersionCompare: (showAlert: boolean) => void;
}

export const VersionBanner: React.FC<Props> = ({ onVersionCompare }: Props) => {
  // LOCAL STATE
  const [showBanner, setShowBanner] = useState<boolean>(false);

  /**
   * Show retry modal.
   *
   * @function
   * @name showRetryModal
   *
   * @description
   * This function displays a modal with a warning message when there is a problem retrieving the latest version of Vantage.
   * The modal provides options to either close and try again or create a Support Ticket by clicking on the Help button.
   *
   * @returns {void} This function does not return anything.
   */
  const showRetryModal = (): void => {
    Modal.warning({
      title: <h2>Well, that didn&apos;t work!</h2>,
      width: 600,
      content: (
        <>
          <Divider />
          <div className="text-lg">
            Close the popup and retry fetching the update. If it fails again, create a Support Ticket using the Help
            button below or check out this{' '}
            <Typography.Link href={CLEAR_SITE_DATA_FAQ} target="_blank">
              FAQ
            </Typography.Link>{' '}
            to clear Vantage site data from your browser.
          </div>
        </>
      )
    });
  };

  useEffect(() => {
    (async () => {
      const url = `${BASE_URL}/public/version`;
      try {
        const reloadOnVersion: string | null = sessionStorage.getItem('reloadOnVersion');
        const { data } = await axios.get(url);
        const showBanner: boolean = data && data !== packageInfo.version;
        setShowBanner(showBanner);
        onVersionCompare(data && data !== packageInfo.version);
        if (reloadOnVersion == packageInfo.version && showBanner) {
          showRetryModal();
        }
        if (!showBanner) sessionStorage.removeItem('reloadOnVersion');
      } catch (e) {
        console.log(e);
      }
    })();
  }, [onVersionCompare]);

  /**
   * Reloads the current web page.
   *
   * @function reloadPage
   * @returns {void} - Does not return any value.
   */
  const reloadPage = (): void => {
    sessionStorage.setItem('reloadOnVersion', packageInfo.version);
    window.location.reload();
  };

  return showBanner ? (
    <Alert
      style={{ position: 'fixed', top: 0, zIndex: 100, width: '100%' }}
      message={
        <div className="text-sm">
          A new and important update is available for Vantage!
          <Button onClick={reloadPage} size="small" type="default" className="ml-2 mr-1">
            Click
          </Button>
          to download and automatically refresh the page.
        </div>
      }
      banner
      type="warning"
      closable={false}
    />
  ) : (
    <div />
  );
};
export default VersionBanner;
